/**
 * StockBadge — universal live-stock display for any part card.
 *
 * RC-211 (2026-08-06). See PERFORMANCE_BIBLE.md §2.4 (memoized) and
 * §2.6 (data-testid on every interactive element).
 *
 * Behavior matrix (matches product spec 2026-08-06):
 *
 *   Viewer type    | untracked | tracked, total=0 | tracked, total>0
 *   ---------------|-----------|-------------------|-------------------------
 *   Customer       | nothing   | nothing           | 🟢 "In Stock" (no qty)
 *   Staff/Admin/*  | nothing   | "In Stock: 0"     | "In Stock: N" · NP:x · KG:y
 *
 * "Staff/Admin/*" = anyone whose user profile has is_admin=true OR a
 * role in the office/warehouse set. Regular customers see the green
 * pill only.
 *
 * Live updates: this component subscribes to a shared Map cache that
 * is fed by (a) a debounced bulk fetch on mount and (b) Pusher
 * `stock_snapshot` events on the `stock-trail-live` channel. Any
 * warehouse scan that touches this JP propagates here in < 2s.
 */

import React, { memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLiveStock } from '../context/LiveStockContext';

const STAFF_ROLES = new Set([
  'admin', 'warehouse_manager', 'warehouse_head', 'office_staff',
  'salesman', 'stock_keeper', 'packer', 'picker', 'supervisor',
  'sub_admin', 'bom_build',
]);

const _isStaffViewer = (user) => {
  if (!user) return false;
  if (user.is_admin === true) return true;
  const role = String(user.role || '').toLowerCase();
  if (role && STAFF_ROLES.has(role)) return true;
  // Warehouse head/manager legacy flags
  if (user.is_warehouse_head || user.is_warehouse_manager) return true;
  return false;
};

const StockBadgeInner = ({ jpPartNo, size = 'md', className = '' }) => {
  const { user } = useAuth();
  const summary = useLiveStock(jpPartNo);
  const staff = _isStaffViewer(user);

  if (!jpPartNo || !summary) return null;
  if (!summary.tracked) return null;

  const total = Number(summary.total || 0);
  const np = Number(summary.nangli_puna || 0);
  const kg = Number(summary.kashmere_gate || 0);

  // Customer view — only show the green pill when there is actual stock.
  if (!staff) {
    if (total <= 0) return null;
    return (
      <span
        data-testid={`stock-badge-customer-${jpPartNo}`}
        className={`inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-0.5 ${className}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
        In Stock
      </span>
    );
  }

  // Staff/Admin view — exact number + per-godown breakdown.
  const sizeCls = size === 'sm'
    ? 'text-[10px] px-1.5 py-0.5'
    : 'text-xs px-2 py-0.5';
  const bgCls = total > 0
    ? 'bg-emerald-100 text-emerald-800'
    : 'bg-slate-100 text-slate-600';
  const dotCls = total > 0 ? 'bg-emerald-500' : 'bg-slate-400';
  return (
    <span
      data-testid={`stock-badge-staff-${jpPartNo}`}
      className={`inline-flex items-center gap-1 rounded-md ${bgCls} ${sizeCls} font-semibold whitespace-nowrap ${className}`}
      title={`Nangli Puna: ${np} · Kashmere Gate: ${kg}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls} inline-block`}></span>
      In Stock: {total}
      <span className="text-[10px] font-normal text-slate-500 ml-1">
        (NP:{np} · KG:{kg})
      </span>
    </span>
  );
};

const StockBadge = memo(StockBadgeInner);
StockBadge.displayName = 'StockBadge';

export default StockBadge;
